import type { EmployeeListItem } from '@/types/employee';

// ============================================================================
// Selection Types
// ============================================================================

/**
 * Represents a selected employee in the picker.
 */
export interface SelectedEmployee {
  id: string;
  firstName: string;
  lastName: string;
  preferredName?: string | null;
  avatarUrl?: string | null;
  workEmail: string;
  department?: { id: string; name: string } | null;
  position?: { id: string; title: string } | null;
  /**
   * Locked entries are part of the selection but cannot be removed from this picker
   * (e.g. employees auto-included via an employment-type / department assignment).
   * They count toward the total, are excluded from the Available list, and are
   * filtered out of the payload sent to the direct-assignment Save endpoint.
   */
  locked?: boolean;
  /** Short reason shown next to a locked employee (e.g. "via Full-time"). */
  lockedReason?: string | null;
}

/**
 * Complete selection state for the picker.
 * Now simplified to just a list of employees.
 */
export interface PickerSelection {
  /** Selected employees */
  employees: SelectedEmployee[];
  /** @deprecated - kept for backward compatibility, will be removed */
  departments: never[];
}

// ============================================================================
// Assignment Mode
// ============================================================================

/**
 * Assignment mode determines how the policy is applied.
 */
export type AssignmentMode = 'all' | 'specific';

// ============================================================================
// Component Props
// ============================================================================

/**
 * Props for the main EmployeePicker component.
 */
export interface EmployeePickerProps {
  /** Current selection state */
  value: PickerSelection;
  /** Callback when selection changes */
  onChange: (selection: PickerSelection) => void;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Show save button */
  showSaveButton?: boolean;
  /** Save button text */
  saveButtonText?: string;
  /** Callback when save is clicked */
  onSave?: () => void;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Whether there are unsaved changes */
  hasChanges?: boolean;
  /** Callback when "Select All" is clicked */
  onSelectAll?: () => void;
  /** Whether "Select All" is loading */
  isLoadingAll?: boolean;
  /**
   * Optional callback to force-exclude a locked (group-inherited) employee
   * from the assignment. When provided, locked rows render an explicit
   * "Exclude" icon-button alongside the lock badge. Implementations should
   * persist the exclusion immediately (the backend's per-employee
   * `excluded=true` override) rather than batching it with Save, since the
   * removed row needs to disappear from the locked list right away.
   */
  onForceRemoveLocked?: (employeeId: string) => Promise<void> | void;
}

/**
 * Props for the AvailableEmployeesPanel.
 */
export interface AvailableEmployeesPanelProps {
  /** IDs of employees already selected */
  excludeEmployeeIds: Set<string>;
  /** IDs of departments already selected (deprecated) */
  excludeDepartmentIds?: Set<string>;
  /** Callback when employees are selected for addition */
  onAddEmployees: (employees: SelectedEmployee[]) => void;
  /** Callback when a department is selected (deprecated) */
  onAddDepartment?: (department: { id: string; name: string; employeeCount: number }) => void;
  /** Whether the panel is disabled */
  disabled?: boolean;
}

/**
 * Props for the SelectedPanel.
 */
export interface SelectedPanelProps {
  /** Current selection */
  selection: PickerSelection;
  /** Callback to remove an employee */
  onRemoveEmployee: (employeeId: string) => void;
  /** Callback to remove a department (deprecated) */
  onRemoveDepartment?: (departmentId: string) => void;
  /** Callback to clear all selection */
  onClearAll: () => void;
  /** Whether the panel is disabled */
  disabled?: boolean;
  /** Callback to force-exclude a locked (group-inherited) employee. See
   *  `EmployeePickerProps.onForceRemoveLocked`. */
  onForceRemoveLocked?: (employeeId: string) => Promise<void> | void;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Transform EmployeeListItem to SelectedEmployee.
 */
export function toSelectedEmployee(employee: EmployeeListItem): SelectedEmployee {
  return {
    id: employee.id,
    firstName: employee.firstName,
    lastName: employee.lastName,
    preferredName: employee.preferredName,
    avatarUrl: employee.avatarUrl,
    workEmail: employee.workEmail,
    department: employee.department,
    position: employee.position,
  };
}

/**
 * Create empty selection.
 */
export function createEmptySelection(): PickerSelection {
  return {
    employees: [],
    departments: [] as never[],
  };
}

/**
 * Get total count of selected employees.
 */
export function getTotalSelectedCount(selection: PickerSelection): number {
  return selection.employees.length;
}
